import type {Area, Asset, NodeBaseMixin, Page, Story} from "./magnolia-types";


export type Delivery<T> = {
  total: number;
  offset: number;
  limit: number;
  results: T[];
}

export type Pages_V1 = Delivery<Page | Area>;
export type Navigation_V1 = Delivery<NavigationRoot>;
export type Stories_V1 = Delivery<Story>;

export type NavigationRoot = NodeBaseMixin<'trimm:navigationRoot'> & {
  [key: string]: NavigationItem | any;
};

export type LinkTypePage = {
  type: 'page';
  // Make sure to have references resolved
  pageLink: NodeBaseMixin<'mgnl:page'> & {
    title?: string;
    '@link': string;
  };
}
export const isLinkTypePage = (link: LinkProperties['link']): link is LinkTypePage => {
  return link?.type === 'page' && link?.pageLink;
}


export type LinkTypeAnchor = {
  type: 'anchor';
  // Make sure to have references resolved
  pageLink?: NodeBaseMixin<'mgnl:page'> & {
    title?: string;
    '@link': string;
  };
  anchorLink: string
}
export const isLinkTypeAnchor = (link: NavigationItem['link']): link is LinkTypeAnchor => {
  return link?.type === 'anchor';
}


export type LinkTypeAsset = {
  type: 'asset';
  // Make sure to have references resolved
  assetLink: NodeBaseMixin<'mgnl:asset'> & {
    '@link': string;
  };
}
export const isLinkTypeAsset = (link: NavigationItem['link']): link is LinkTypeAsset => {
  return link?.type === 'asset' && link?.assetLink;
}

export type LinkTypeExternal = {
  type: 'external';
  externalLink: string;
  externalTarget?: "_blank" | "_self";
}
export const isLinkTypeExternal = (link: NavigationItem['link']): link is LinkTypeExternal => {
  return link?.type === 'external' && link?.externalLink;
}


export type LinkTypeNone = {
  type: 'none';
}
export const isLinkTypeNone = (link: NavigationItem['link']): link is LinkTypeNone => {
  return link?.type === 'none';
}

export type LinkTypes = LinkTypePage | LinkTypeAnchor | LinkTypeAsset | LinkTypeExternal | LinkTypeNone;

export type LinkProperties = {
  title?: string;
  link?: LinkTypes;
}

export type NavigationItem = NodeBaseMixin<'trimm:navigationItem'> & {
  [key: string]: NavigationItem | any;
} & LinkProperties;


export const isNode = (node: any): node is NodeBaseMixin<string> => {
  return node && Object.hasOwnProperty.call(node, '@nodeType');
}


export const isNavigationRoot = (node: any): node is  NavigationRoot => {
  return isNode(node) && node["@nodeType"] === 'trimm:navigationRoot';
}

export const isNavigationItem = (node: any): node is NavigationItem => {
  return isNode(node) && node["@nodeType"] === 'trimm:navigationItem';
}

export const isAsset = (node: any): node is Asset => {
  return node && Object.hasOwnProperty.call(node, 'metadata') && Object.hasOwnProperty.call(node, '@link')
    && node['@link'].includes('/dam/jcr:');
}

export const isComponent = (node: any): node is Component => {
  return isNode(node) && node['@nodeType'] === 'mgnl:component';
}


export const isArea = (node: any): node is Area => {
  return isNode(node) && node['@nodeType'] === 'mgnl:area';
}

export const isPage = (node: any): node is Page => {
  return isNode(node) && node['@nodeType'] === 'mgnl:page';
}

